import {UploadHistoryComponent} from '../upload-history/upload-history.component';
import {hasUploadAccess, isManualUploadProvider} from '../upload-history/upload-access';
import {TranslateDirective} from '../../../../common/utils/translate.directive';
import {Component, Input, OnInit} from '@angular/core';
import {View} from "src/app/common/view";
import {defaultModalOptions} from '../../../../common/modal/modal';
import {Handler} from "src/app/common/handler";
import {
    Connection,
    ConnectionInfo,
    ElectricityConnectionInfo,
    EnnatuurlijkMeter,
    GasConnectionInfo,
    HeatConnectionInfo,
    Location,
    MeterType,
    Organisation,
    UpsertConnection,
    UpsertEnecoMeter
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {AppContext} from "../../../../app-context";
import {MeterMetadataField, RefdataUtils} from "../../refdata-utils";
import {uuid} from "../../../../common/utils";
import {Entity} from "../../../../handlers/entity";
import {Observable} from "rxjs";
import {Moment} from "moment/moment";
import moment from "moment";
import {AppCommonUtils} from "../../../../common/app-common-utils";
import {cloneDeep} from "lodash";
import {
    calculatedResidualEntity,
    hasActiveSourceBpm,
    hasActiveSourceChargePoint,
    isCalculatedResidual
} from '../../meters/submeter-role.utils';
import {openConfirmationModalWithCallback} from '../../../../app-utils';
import {
    ModalConfirmAutofocus,
    ModalConfirmAutofocusData
} from '../../../../common/modal-confirm/modal-confirm.component';
import {
    downloadIntermediateMeterMetadata,
    readIntermediateMeterMetadata,
    SubmeterMetadataUpdate,
    validateIntermediateMeterMetadata
} from "../connections-overview/intermediate-meter-metadata-excel";

@Component({
  selector: 'app-connection-details',
  templateUrl: './connection-details.component.html',
  styleUrls: ['./connection-details.component.scss']
})
@Handler()
export class ConnectionDetailsComponent extends View implements OnInit {
  appContext = AppContext;
  refDataUtils = RefdataUtils;
  @Input() data: ConnectionComponentData;

  newConnectionId: string;
  connectionTypes: string[] = ["Electricity", "Gas", "Heat", "Water", "ChargePoint"];
  marketSegments: string[] = ["KV", "GV"];
  location: Entity;
  info: ConnectionInfo;
  enecoMeterNumber: string;
  isNewConnection: boolean;
  minDesiredEndDate: Moment;
  intermediateMeters: Entity[] = [];
  coolingMeters: EnnatuurlijkMeter[] = [];
  coolingMeter: EnnatuurlijkMeter;
  private coolingLookupCode: string;
  coolingMeterFormatter = (meter: EnnatuurlijkMeter) =>
    meter ? [meter.name, meter.address || meter.ean].filter(Boolean).join(' — ') : null;

  get canAddCooling(): boolean {
    return this.isEnnatuurlijkConnection && !this.isNewConnection && this.info.connectionType === 'Heat'
      && this.data.connection?.meters?.some(m => m.active !== false && m.info.type === MeterType.PRIMARY)
      && !this.data.connection?.meters?.some(m => m.active !== false && m.info.type === MeterType.COOLING);
  }

  loadCoolingMeters(): void {
    const code = this.canAddCooling && this.appContext.isManagerForEntity(this.location) ? this.info.code : null;
    if (this.coolingLookupCode === code) return;
    this.coolingLookupCode = code;
    this.coolingMeters = [];
    this.coolingMeter = null;
    if (!code) return;
    this.sendQuery('com.flowmaps.api.ennatuurlijk.GetEnnatuurlijkMeters', {
      organisationId: this.data.organisation.organisationId, locationId: this.data.location.locationId
    }).subscribe(result => {
      if (!this.canAddCooling || this.info.code !== code) return;
      const cooling = result.meters.filter(m => m.connectionType === 'Cooling');
      // A shared EAN is linked automatically by the ordinary save action.
      this.coolingMeters = cooling.some(m => m.ean && m.ean === code) ? [] : cooling;
    });
  }

  get canOpenUploadHistory(): boolean {
    return !this.isNewConnection && hasUploadAccess(this.data.connection);
  }

  get showUploadHistory(): boolean {
    return this.canOpenUploadHistory || isManualUploadProvider(this.info?.surveyor?.code);
  }

  uploadHistoryText(key: string): string {
    return TranslateDirective.getTranslation(key.toLowerCase(), key) as string;
  }

  openUploadHistory(): void {
    if (!this.canOpenUploadHistory) return;
    const trigger = document.activeElement as HTMLElement;
    // Open at the app root: a nested Bootstrap dialog is clipped by the connection form's overflow/transform.
    this.sendCommandAndForget('openModal', {
      component: UploadHistoryComponent,
      data: {
        organisationId: this.data.organisation.organisationId,
        locationId: this.data.location.locationId,
        preferredConnectionId: this.data.connection.connectionId
      },
      options: {...defaultModalOptions, closeOnSuccess: false, scrollable: true,
        cssClass: 'modal-xl upload-history-modal', closeCallback: () => trigger?.focus({preventScroll: true})}
    });
  }

  get isEnnatuurlijkConnection(): boolean {
    return this.info.surveyor?.code === 'energiemissie';
  }

  get infoAsElectricity(): ElectricityConnectionInfo {
    return this.info as ElectricityConnectionInfo;
  }

  get infoAsGas(): GasConnectionInfo {
    return this.info as GasConnectionInfo;
  }

  get infoAsHeat(): HeatConnectionInfo {
    return this.info as HeatConnectionInfo;
  }

  get isEnecoConnection(): boolean {
    return this.info.surveyor?.code === "089349d8-db89-447e-a838-79a0794997cf";
  }

  get contractedCapacity() {
      return this.data.connection?.contractedCapacity || this.info.connectionValue?.contractedCapacity || this.info.contractedCapacity;
  }

  ngOnInit(): void {
    if (!this.data.connection) {
      this.isNewConnection = true;
      this.newConnectionId = uuid();
    }
    this.info = cloneDeep(this.data.connection?.info) || <ConnectionInfo>{};
    if (this.info.connectionType === 'Cooling') this.connectionTypes.push('Cooling');
    if (this.isEnecoConnection) {
      this.enecoMeterNumber = this.data.connection?.meters?.find(m => m.meterId.startsWith("eneco-"))?.info?.meterNumbers[0];
    }
    if (this.data.location) {
      this.location = new Entity(this.data.organisation, this.data.location)
    }
    this.loadIntermediateMeters();
    this.loadCoolingMeters();
    this.updateMinDate();
  }

  downloadIntermediateMeters = () => {
    if (this.intermediateMeters.length) {
      downloadIntermediateMeterMetadata(this.intermediateMeters[0], this.intermediateMeters);
    }
  };

  uploadIntermediateMeters(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    readIntermediateMeterMetadata(file).subscribe({
      next: rows => {
        const validation = validateIntermediateMeterMetadata(rows, this.intermediateMeters);
        if (validation.errors.length) {
          validation.errors.forEach(error => AppCommonUtils.registerError(error));
          return;
        }
        this.confirmBulkSourceOverlap(validation.updates, () => this.applyMetadataUpdates(validation.updates));
      },
      error: error => AppCommonUtils.registerError(error)
    });
  }

  private confirmBulkSourceOverlap(updates: SubmeterMetadataUpdate[], proceed: () => void): void {
    const addsBpm = updates.some(update => update.row.visible && update.row.grossProduction
      && update.entity.meter.info.type !== MeterType.GROSS_PRODUCTION);
    const addsChargePoint = updates.some(update => update.row.visible && update.row.chargePoint);
    const bpmOverlap = addsBpm && hasActiveSourceBpm(this.data.location);
    const chargePointOverlap = addsChargePoint && hasActiveSourceChargePoint(this.data.location);
    if (!bpmOverlap && !chargePointOverlap) {
      proceed();
      return;
    }
    const message = bpmOverlap && chargePointOverlap
      ? 'Active gross production and charge point data are already linked. If you continue, source data and manually classified meters will be added together.'
      : bpmOverlap
        ? 'Active gross production data is already linked. If you continue, source data and manually classified meters will be added together.'
        : 'Active charge point data is already linked. If you continue, source data and manually classified meters will be added together.';
    openConfirmationModalWithCallback(confirmed => {
      if (confirmed) proceed();
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: 'warning',
      title: 'Use multiple meter sources',
      message,
      confirmText: 'Use both sources',
      cancelText: 'Cancel'
    }, 'static');
  }

  private loadIntermediateMeters() {
    const connection = this.data.connection;
    const location = this.data.location;
    if (!connection || !location || connection.info.connectionType !== 'Electricity') {
      this.intermediateMeters = [];
      return;
    }
    this.intermediateMeters = connection.meters
      .filter(meter => meter.info.type === MeterType.INTERMEDIATE || isCalculatedResidual(meter))
      .map(meter => new Entity(this.data.organisation, location, connection, meter));
    if (!this.intermediateMeters.some(entity => isCalculatedResidual(entity.meter))) {
      const residual = calculatedResidualEntity(this.data.organisation, location, connection);
      if (residual) this.intermediateMeters.push(residual);
    }
  }

  private applyMetadataUpdates(updates: SubmeterMetadataUpdate[], index = 0) {
    if (index >= updates.length) {
      AppCommonUtils.registerSuccess('Intermediate meter metadata was updated successfully.');
      return;
    }
    const {entity, row} = updates[index];
    const details: any = cloneDeep(entity.meter.details) || {
      type: entity.meter.info.type,
      meterNumbers: [],
      tags: []
    };
    details.label = nullable(row.label);
    this.applyMetadataTag(details, entity, 'tenant', row.tenant);
    this.applyMetadataTag(details, entity, 'floor', row.floor);
    this.applyMetadataTag(details, entity, 'tower', row.tower);
    this.applyMetadataTag(details, entity, 'wing', row.wing);
    details.area = row.area;
    details.visible = row.visible;
    details.chargePoint = isCalculatedResidual(entity.meter) ? false : row.chargePoint;
    details.type = !isCalculatedResidual(entity.meter) && row.grossProduction
      ? MeterType.GROSS_PRODUCTION : MeterType.INTERMEDIATE;
    const updateDetails = cloneDeep(details);
    const command = isCalculatedResidual(entity.meter)
      ? 'com.flowmaps.api.organisation.UpsertCalculatedResidualMeterDetails'
      : 'com.flowmaps.api.organisation.UpdateMeterDetails';
    this.sendCommand(command, {
      organisationId: entity.organisation.organisationId,
      locationId: entity.location.locationId,
      connectionId: entity.connection.connectionId,
      ...(isCalculatedResidual(entity.meter) ? {} : {meterId: entity.meter.meterId}),
      details: updateDetails
    }, () => {
      entity.meter.details = updateDetails;
      this.applyMetadataUpdates(updates, index + 1);
    });
  }

  private applyMetadataTag(details, entity: Entity, field: MeterMetadataField, value: string): void {
    if (!RefdataUtils.hasSourceMeterMetadataValue(entity.meter, field)) {
      RefdataUtils.setMeterMetadataDetailTag(details, field, nullable(value));
    }
  }

  onLocationSelected = (entity: Entity) => {
    this.data.organisation = entity.organisation;
  }

  searchLocation = (term: string): Observable<Entity[]> =>
    this.data.organisation?.organisationId ?
        this.sendQuery("getLocationsPerOrganisation", {
          organisationId: this.data.organisation.organisationId,
          term: term
        }) : this.sendQuery("searchLocations", term);

  updateInfo = () => {
    if (this.isEnnatuurlijkConnection) {
      return this.sendCommand('com.flowmaps.api.ennatuurlijk.UpsertEnnatuurlijkConnection', {
        organisationId: this.data.organisation.organisationId,
        locationId: this.location.location.locationId,
        connectionId: this.newConnectionId || this.data.connection?.connectionId,
        info: this.info,
        ...(this.canAddCooling && this.coolingMeter ? {coolingMeterUuid: this.coolingMeter.uuid} : {})
      });
    }
    if (this.isEnecoConnection) {
      return this.sendCommand("com.flowmaps.api.organisation.UpsertEnecoMeter", <UpsertEnecoMeter> {
        organisationId: this.data.organisation.organisationId,
        locationId: this.location.location.locationId,
        connectionId: this.newConnectionId || this.data.connection?.connectionId,
        info: this.infoAsHeat,
        meterNumber: this.enecoMeterNumber
      });
    } else {
      return this.sendCommand("com.flowmaps.api.organisation.UpsertConnection", <UpsertConnection> {
        organisationId: this.data.organisation.organisationId,
        locationId: this.location.location.locationId,
        connectionId: this.newConnectionId || this.data.connection?.connectionId,
        info: this.info
      });
    }
  };

  updateMinDate() {
    this.minDesiredEndDate = moment().startOf('day');
  }
}

function nullable(value: string): string | null {
  const result = value?.trim();
  return result ? result : null;
}

export interface ConnectionComponentData {
  organisation: Organisation;
  location?: Location;
  connection?: Connection;
}
